import useTranslation from 'next-translate/useTranslation'
import { useCallback, useContext } from 'react'
import { faArrowsToDot, faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons'
import { IconButton } from '@unreserved-frontend-v2/ui/icon-button/icon-button'
import { MapContext } from './map-context'
import {
  DEFAULT_BOUNDS_OFFSET,
  DEFAULT_BOUNDS_PADDING,
  DEFAULT_LISTING_ZOOM,
  FIT_TO_BOUNDS_DEFAULT_DURATION,
} from '../constants'
import { twMerge } from 'tailwind-merge'
import { LngLatBoundsLike } from 'maplibre-gl'
import { fitMapBounds } from '../utils'

export interface BaseInteractionsProps {
  bounds?: LngLatBoundsLike | null
  containerClassName?: string
  showZoomControls?: boolean
  showCenterControl?: boolean
  offsetControls?: boolean
  fitToBoundsPadding?: number
  fitToBoundsOffset?: [number, number]
  fitToBoundsDuration?: number
  fitToBoundsMaxZoom?: number
}

export function BaseInteractions({
  bounds,
  showZoomControls = true,
  showCenterControl = true,
  offsetControls = false,
  containerClassName = '',
  fitToBoundsPadding = DEFAULT_BOUNDS_PADDING,
  fitToBoundsOffset = DEFAULT_BOUNDS_OFFSET,
  fitToBoundsDuration = FIT_TO_BOUNDS_DEFAULT_DURATION,
  fitToBoundsMaxZoom = DEFAULT_LISTING_ZOOM,
}: BaseInteractionsProps) {
  const { map } = useContext(MapContext)
  const { t } = useTranslation()

  const handleCenterClick = useCallback(
    (e: React.BaseSyntheticEvent) => {
      if (e.cancelable) {
        e.preventDefault()
      }

      fitMapBounds(map, bounds, {
        bearing: 0,
        pitch: 0,
        duration: fitToBoundsDuration,
        padding: fitToBoundsPadding,
        offset: fitToBoundsOffset,
        maxZoom: fitToBoundsMaxZoom,
      })
    },
    [bounds, fitToBoundsDuration, fitToBoundsMaxZoom, fitToBoundsOffset, fitToBoundsPadding, map]
  )

  const handleZoomIn = useCallback(
    (e: React.BaseSyntheticEvent) => {
      if (e.cancelable) {
        e.preventDefault()
      }

      map?.flyTo({ zoom: map.getZoom() + 1 })
    },
    [map]
  )

  const handleZoomOut = useCallback(
    (e: React.BaseSyntheticEvent) => {
      if (e.cancelable) {
        e.preventDefault()
      }

      map?.flyTo({ zoom: map.getZoom() - 1 })
    },
    [map]
  )

  return (
    <div
      className={twMerge(
        `absolute z-[100]`,
        offsetControls ? 'top-[78px] right-[24px]' : 'top-4 right-4',
        containerClassName
      )}
    >
      {showZoomControls ? (
        <>
          <IconButton
            className="border-shades-200 rounded-b-[0] text-xl"
            icon={faPlus}
            onClick={handleZoomIn}
            aria-label={t('listings:increase-zoom')}
          />
          <IconButton
            className="border-shades-200 rounded-t-[0] border-t-0 text-xl"
            icon={faMinus}
            onClick={handleZoomOut}
            aria-label={t('listings:decrease-zoom')}
          />
        </>
      ) : null}

      {showCenterControl ? (
        <IconButton
          className="border-shades-200 mt-3 text-xl"
          icon={faArrowsToDot}
          onClick={handleCenterClick}
          aria-label={t('listings:recentre')}
        />
      ) : null}
    </div>
  )
}

export default BaseInteractions
